import { useEffect, useRef, useState } from 'react';
import { useForm } from '@mantine/form';
import { Alert, Box, Button, Group, Loader, Stack, Text, Title } from '@mantine/core';
import { modals } from '@mantine/modals';
import { notifications } from '@mantine/notifications';
import { IconAlertCircle, IconPlus } from '@tabler/icons-react';
import { fuelAshCategoriesApi, type FuelAshCategory } from '../../services/fuelAshCategoriesApi';
import { getApiErrorMessage, getApiViolations } from '../../utils/errorHandling';
import { mapViolations } from '../../utils/mapViolations';
import { prefixViolationPaths } from '../masterdata/violationPaths';
import { FuelAshCategoryTable } from './FuelAshCategoryTable';
import {
  createEmptySecondaryRow,
  deleteSuccessMessage,
  footnoteSummary,
  isUnnamedRow,
  persistedRowCount,
  removeRowFromBaseline,
  replaceRowInBaseline,
  RowSaveError,
  saveChangedRows,
  toFormValues,
  type FuelAshFormValues,
  type FuelAshRowState,
} from './fuelAshForm';
import classes from './FuelAshCategoriesPanel.module.css';

interface FuelAshCategoriesPanelProps {
  /** Meldet die Zahl gespeicherter Zeilen (beide Abschnitte) an `SettingsPage`
   *  (Navigationszähler, Sketch 018) — anders als bei KANÜ hier tatsächlich variabel. */
  onCountChange: (count: number) => void;
}

/** Erklärt den Haken „Grenzen bindend" — steht genau einmal über beiden Tabellen, weil er für
 *  beide gilt (D-02). Als String-Konstante statt JSX-Text, damit der Satz in einer Zeile steht
 *  und die eigene Acceptance-Prüfung diesen Wortlaut greifen kann. */
const BINDING_LEGEND =
  'Grenzen bindend: Werte außerhalb des Bereichs werden im Formular abgewiesen. Ohne Haken erscheint nur ein Hinweis.';

/**
 * Umweltdaten-Bereich der Einstellungsseite (Sketch 018 Variante B, D-19): zwei Tabellen
 * (Primär-/Sekundärbrennstoffe) unter einem gemeinsamen Speichern-Knopf. Anlegen und Löschen
 * einer Sekundärkategorie wirken sofort (D-06/D-12); nur die Feldwerte selbst sammeln sich bis
 * zum Speichern-Knopf. Feldfehler kommen ausschließlich vom Backend
 * (`form.setErrors(mapViolations(...))`), keine eigene Prüfregel-Konfiguration im Formular.
 */
export function FuelAshCategoriesPanel({ onCountChange }: FuelAshCategoriesPanelProps) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const newRowCounterRef = useRef(0);

  // controlled, weil Zeilen, Fussnoten und aria-labels im Render aus form.getValues() lesen und
  // Anlegen/Loeschen sofort neu rendern muessen. Der Speichern-Knopf haengt nach D-23 nicht mehr
  // am Aenderungsstand (Muster ReportEditPage.tsx:864).
  const form = useForm<FuelAshFormValues>({
    mode: 'controlled',
    initialValues: { primary: [], secondary: [] },
  });

  function applyLoaded(categories: FuelAshCategory[]) {
    const next = toFormValues(categories);
    // form.setValues(next) + form.resetDirty(next) setzen Werte und Vergleichsstand gemeinsam.
    // form.getInitialValues() liest diesen Stand, saveChangedRows() ueberspringt dagegen
    // unveraenderte Zeilen -- deshalb schickt ein Speichern ohne Aenderung keinen Request (D-23).
    form.setValues(next);
    form.resetDirty(next);
    form.clearErrors();
    onCountChange(persistedRowCount(next));
  }

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        setLoading(true);
        setLoadError(null);
        const categories = await fuelAshCategoriesApi.getAll();
        if (cancelled) return;
        applyLoaded(categories);
      } catch (err) {
        if (!cancelled) {
          setLoadError(getApiErrorMessage(err, 'Fehler beim Laden der Aschegehalte'));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void load();

    return () => {
      cancelled = true;
    };
    // Laedt genau einmal beim Mounten; `applyLoaded` liest `form` ueber dessen stabile
    // useForm-Identitaet, ein erneuter Lauf bei jedem Render waere wirkungslos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Bringt eine neu angelegte Zeile in Sicht (D-12: sie bleibt am Ort der Anlage, bis
  // gespeichert wird) -- der Fokus selbst sitzt bereits am `autoFocus`-Feld in
  // `FuelAshCategoryTable`, dieser Effekt ergaenzt nur die Sichtbarkeit bei langen Tabellen.
  useEffect(() => {
    if (!focusKey) return;
    const element = document.querySelector(`[data-row-key="${focusKey}"]`);
    element?.scrollIntoView({ block: 'center' });
  }, [focusKey]);

  function handleAddRow() {
    newRowCounterRef.current += 1;
    const counter = newRowCounterRef.current;
    form.insertListItem('secondary', createEmptySecondaryRow(counter));
    setFocusKey(`new-${counter}`);
  }

  function handleDeleteRowClick(index: number) {
    const row = form.getValues().secondary[index];
    if (!row || row.catchAll) {
      return;
    }
    // Reine Sicherheitsabfrage (Sketch 018 README) -- nur Frage und Ja/Nein, kein zusaetzlicher
    // Schliessen-Knopf neben "Abbrechen"/"Löschen" (F2, Nacharbeit 33-08). Escape und Klick auf
    // den Hintergrund schliessen den Dialog weiterhin.
    modals.openConfirmModal({
      title: 'Kategorie löschen?',
      size: 440,
      withCloseButton: false,
      children: (
        <Text size="sm">
          {isUnnamedRow(row.name) ? (
            'Soll die neue Kategorie wirklich gelöscht werden?'
          ) : (
            <>
              Soll die Kategorie <b>{row.name.trim()}</b> wirklich gelöscht werden?
            </>
          )}
        </Text>
      ),
      labels: { confirm: 'Löschen', cancel: 'Abbrechen' },
      confirmProps: { color: 'red', 'data-autofocus': true },
      onConfirm: () => void handleDeleteConfirmed(row),
    });
  }

  async function handleDeleteConfirmed(row: FuelAshRowState) {
    setFormError(null);
    try {
      if (row.id) {
        await fuelAshCategoriesApi.delete(row.id);
      }
      // Vergleichsstand VOR dem Entfernen aus der Liste nachziehen -- sonst gilt das Loeschen
      // selbst als ungespeicherte Aenderung.
      form.setInitialValues(removeRowFromBaseline(form.getInitialValues(), row.key));
      const currentIndex = form.getValues().secondary.findIndex((candidate) => candidate.key === row.key);
      if (currentIndex !== -1) {
        form.removeListItem('secondary', currentIndex);
      }
      onCountChange(persistedRowCount(form.getValues()));
      notifications.show({ title: 'Gelöscht', message: deleteSuccessMessage(row.name), color: 'green' });
    } catch (err) {
      setFormError(getApiErrorMessage(err, 'Fehler beim Löschen'));
    }
  }

  async function handleSubmit(values: FuelAshFormValues) {
    setFormError(null);
    setSaving(true);
    try {
      await saveChangedRows(values, form.getInitialValues(), fuelAshCategoriesApi, (event) => {
        // Vergleichsstand UND `id` sofort nachziehen -- ein zweiter Speicherversuch nach einem
        // Teilfehler legt diese Zeile sonst doppelt an (T-33-25).
        const savedRow: FuelAshRowState = { ...event.row, id: event.saved.id };
        form.setInitialValues(replaceRowInBaseline(form.getInitialValues(), event.section, savedRow));
        form.setFieldValue(`${event.section}.${event.index}.id`, event.saved.id);
      });
      applyLoaded(await fuelAshCategoriesApi.getAll());
      setFocusKey(null);
      notifications.show({
        title: 'Gespeichert',
        message: 'Die Einstellungen wurden übernommen.',
        color: 'green',
      });
    } catch (err) {
      if (err instanceof RowSaveError) {
        form.setErrors(mapViolations(prefixViolationPaths(getApiViolations(err.cause), err.failingPath)));
        setFormError(getApiErrorMessage(err.cause, 'Fehler beim Speichern'));
      } else {
        form.setErrors(mapViolations(getApiViolations(err)));
        setFormError(getApiErrorMessage(err, 'Fehler beim Speichern'));
      }
    } finally {
      setSaving(false);
    }
  }

  const primarySummary = footnoteSummary(form.getValues().primary);
  const secondarySummary = footnoteSummary(form.getValues().secondary);
  const primaryFootnote =
    `${primarySummary.total} Brennstoffe, fest` +
    (primarySummary.withoutStartValue > 0 ? ` · ${primarySummary.withoutStartValue} ohne Startwert` : '');
  const secondaryFootnote = `${secondarySummary.total} Kategorien`;

  return (
    <Stack gap="md">
      {loadError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" onClose={() => setLoadError(null)} withCloseButton>
          {loadError}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="md">
          <Loader size="sm" />
        </Group>
      ) : (
        <Box component="form" onSubmit={form.onSubmit(handleSubmit)}>
          {formError && (
            <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
              {formError}
            </Alert>
          )}

          <Text size="xs" c="dimmed" mb="md" data-testid="fuel-ash-legend">
            {BINDING_LEGEND}
          </Text>

          <Title order={3}>Aschegehalt — Primärbrennstoffe</Title>
          <Text size="sm" c="dimmed" mb="sm">
            Sieben feste Brennstoffe, vorgegeben durch das Formular. Startwert, Bereich und
            Verbindlichkeit sind pflegbar; die Liste selbst nicht.
          </Text>
          <FuelAshCategoryTable form={form} section="primary" disabled={saving} focusKey={focusKey} />
          <Text size="xs" c="dimmed" className={classes.footnote}>
            {primaryFootnote}
          </Text>

          <div className={classes.subblock}>
            <Group justify="space-between" align="flex-start" wrap="nowrap" mb="sm">
              <div>
                <Title order={3}>Aschegehalt — Sekundär-/Ersatzbrennstoffe</Title>
                <Text size="sm" c="dimmed">
                  Frei pflegbar. Diese Liste bestimmt, welche Kategorien der Melder im Formular zur
                  Auswahl hat.
                </Text>
              </div>
              <Button
                variant="default"
                size="xs"
                leftSection={<IconPlus size={14} />}
                onClick={handleAddRow}
                disabled={saving}
              >
                Kategorie
              </Button>
            </Group>
            <FuelAshCategoryTable
              form={form}
              section="secondary"
              disabled={saving}
              focusKey={focusKey}
              onDeleteRow={handleDeleteRowClick}
            />
            <Text size="xs" c="dimmed" className={classes.footnote}>
              {secondaryFootnote}
            </Text>
          </div>

          <Group className={classes.savebar} gap="md">
            <Button type="submit" disabled={saving} loading={saving}>
              Aschegehalte speichern
            </Button>
            <Text size="xs" c="dimmed">
              Startwerte gelten ab dem nächsten Anlegen einer Meldung, Grenzen sofort für alle offenen Meldungen.
            </Text>
          </Group>
        </Box>
      )}
    </Stack>
  );
}
